import React, { createContext, ReactNode, useContext, useState } from 'react';

import { EntityType } from 'types/entityType';
import { TTableEntity } from 'types/tableEntityType';
import { areAllEntitiesSelected } from 'utils/areAllEntitiesSelected';
import { DraftStatus, LiveStatus } from 'utils/statuses';

interface ISelectedEntitiesContext {
  selectedEntities: TTableEntity[];
  selectedEntityIds: (type?: EntityType.FRAME | EntityType.LINKED_ASSET) => string[];
  isEntitySelected: (entityId: string) => boolean;
  areEntitiesSelected: boolean;
  numberOfSelectedEntitiesInStatus: (status: LiveStatus | DraftStatus) => number;
  areSelectedEntitiesInSameStatus: (status: LiveStatus | DraftStatus) => boolean;
  updateSelectedEntities: (entityId: string, providedEntities: TTableEntity[]) => void;
  resetSelectedEntities: () => void;
  toggleSelectAllEntities: (providedEntities: TTableEntity[]) => void;
}

const SelectedEntitiesContext = createContext<ISelectedEntitiesContext | undefined>(undefined);

export const useSelectedEntitiesContext = () => {
  const context = useContext(SelectedEntitiesContext);
  if (!context) {
    throw new Error('useSelectedEntities must be used within a SelectedEntitiesProvider');
  }
  return context;
};

type SelectedEntitiesProviderProps = {
  children: ReactNode;
};

export const SelectedEntitiesProvider: React.FC<SelectedEntitiesProviderProps> = ({ children }) => {
  const [internalSelectedEntities, setInternalSelectedEntities] = useState<TTableEntity[]>([]);

  const getSelectedEntityIds = (type?: EntityType.FRAME | EntityType.LINKED_ASSET) => {
    return internalSelectedEntities
      .filter((entity) => !type || entity.entityType === type)
      .map((entity) => entity.spaceId);
  };

  const isEntitySelected = (entityId: string) => {
    return internalSelectedEntities.some((entity) => entity.spaceId === entityId);
  };

  const areEntitiesSelected = internalSelectedEntities && internalSelectedEntities.length > 0;

  const numberOfSelectedEntityMatchingStatus = (status: LiveStatus | DraftStatus) => {
    return internalSelectedEntities.filter((entity) => entity.status === status).length;
  };

  const areSelectedEntitiesInSameStatus = (status: LiveStatus | DraftStatus) => {
    return internalSelectedEntities.every((entity) => entity.status === status);
  };

  const updateSelectedEntities = (entityId: string, providedEntities: TTableEntity[]) => {
    const entityToUpdate = providedEntities.find((item) => item.spaceId === entityId);
    if (entityToUpdate) {
      setInternalSelectedEntities((prev) => {
        const isEntityAlreadySelected = prev.some((entity) => entity.spaceId === entityId);
        return isEntityAlreadySelected
          ? prev.filter((entity) => entity.spaceId !== entityId)
          : [...prev, entityToUpdate];
      });
    }
  };

  const resetSelectedEntities = () => {
    setInternalSelectedEntities([]);
  };

  const toggleSelectAllEntities = (providedEntities: TTableEntity[]) => {
    const allSelected = areAllEntitiesSelected(internalSelectedEntities, providedEntities);

    if (allSelected) {
      resetSelectedEntities();
    } else {
      setInternalSelectedEntities(providedEntities);
    }
  };

  const selectedEntitiesContextValue = {
    selectedEntities: internalSelectedEntities,
    selectedEntityIds: getSelectedEntityIds,
    isEntitySelected: isEntitySelected,
    areEntitiesSelected: areEntitiesSelected,
    numberOfSelectedEntitiesInStatus: numberOfSelectedEntityMatchingStatus,
    areSelectedEntitiesInSameStatus: areSelectedEntitiesInSameStatus,
    updateSelectedEntities: updateSelectedEntities,
    resetSelectedEntities: resetSelectedEntities,
    toggleSelectAllEntities: toggleSelectAllEntities,
  };

  return (
    <SelectedEntitiesContext.Provider value={selectedEntitiesContextValue}>
      {children}
    </SelectedEntitiesContext.Provider>
  );
};

export default SelectedEntitiesProvider;
